import React from 'react';
import { useSeller } from '../../context/SellerContext';
import InputField from '../InputField';
import FAQSection from '../FAQSection';
import {
  validateStoreName,
  validateFullName,
  validateEmail,
} from '../../utils/validation';
import { sellerAuthAPI, handleAPIError } from '../../utils/sellerAPI';

const SupplierDetailsForm = ({ onNext, onPrev, onSuccess }) => {
  const {
    formData,
    updateFormData,
    errors,
    setError,
    clearAllErrors,
    loading,
    setLoading,
    setAuthData,
  } = useSeller();

  const handleStoreNameChange = (e) => {
    updateFormData('storeName', e.target.value);
    clearAllErrors('storeName');
  };

  const handleSupplierNameChange = (e) => {
    updateFormData('supplierFullName', e.target.value);
    clearAllErrors('supplierFullName');
  };

  const handleEmailChange = (e) => {
    updateFormData('supplierEmail', e.target.value);
    clearAllErrors('supplierEmail');
  };

  const handleAgreeChange = (e) => {
    updateFormData('agreeToTerms', e.target.checked);
    clearAllErrors('agreeToTerms');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    const validationErrors = {};
    const storeError = validateStoreName(formData.storeName);
    const nameError = validateFullName(formData.supplierFullName);
    const emailError = validateEmail(formData.supplierEmail);

    if (storeError) validationErrors.storeName = storeError;
    if (nameError) validationErrors.supplierFullName = nameError;
    if (emailError) validationErrors.supplierEmail = emailError;

    if (!formData.agreeToTerms) {
      validationErrors.agreeToTerms = 'You must agree to the Supplier Agreement';
    }

    if (Object.keys(validationErrors).length > 0) {
      Object.entries(validationErrors).forEach(([field, error]) => {
        setError(field, error);
      });
      return;
    }

    setLoading(true);
    try {
      const registrationData = {
        mobileNumber: formData.mobileNumber,
        storeName: formData.storeName,
        supplierFullName: formData.supplierFullName,
        supplierEmail: formData.supplierEmail,
        hasGST: formData.hasGST,
        gstNumber: formData.hasGST ? formData.gstNumber : null,
        panNumber: formData.panNumber,
        enrolmentId: formData.enrolmentId,
        pickupAddress: formData.pickupAddress,
        bankDetails: formData.bankDetails,
      };

      const response = await sellerAuthAPI.completeRegistration(registrationData);

      if (response.status === 200) {
        setAuthData(response.data.token, response.data.seller);
        onSuccess();
      }
    } catch (err) {
      const apiError = handleAPIError(err);
      setError('submit', apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const supplierFAQs = [
    {
      question: 'What is Store Name and where it is used?',
      answer:
        'Your Store Name is visible across the Kalinga Crafts Store marketplace with your products. Buyers use it to identify their preferred suppliers.',
    },
    {
      question: 'Can I change my Store Name later?',
      answer: 'Yes, you can update your Store Name anytime from your seller dashboard settings.',
    },
  ];

  return (
    <div className="w-full">
      <div className="max-w-2xl mx-auto">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Info Message */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800 font-medium">
              ℹ️ Your store name will be visible to all buyers on Kalinga Crafts Store
            </p>
          </div>

          {/* Supplier Details Form */}
          <div className="bg-white rounded-lg p-6 border border-gray-200 space-y-4">
            <InputField
              label="Store Name"
              type="text"
              name="storeName"
              value={formData.storeName}
              onChange={handleStoreNameChange}
              placeholder="Eg. Business Name, Trade Name, etc."
              error={errors.storeName}
              required
            />

            <InputField
              label="Your Full Name"
              type="text"
              name="supplierFullName"
              value={formData.supplierFullName}
              onChange={handleSupplierNameChange}
              placeholder="Enter your full name"
              error={errors.supplierFullName}
              required
            />

            <InputField
              label="Email ID"
              type="email"
              name="supplierEmail"
              value={formData.supplierEmail}
              onChange={handleEmailChange}
              placeholder="Enter email address"
              error={errors.supplierEmail}
              required
            />
          </div>

          {/* Agreement Checkbox */}
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <label className="flex items-start">
              <input
                type="checkbox"
                checked={formData.agreeToTerms}
                onChange={handleAgreeChange}
                className="w-4 h-4 text-blue-600 mt-1"
              />
              <span className="ml-3 text-sm text-gray-700">
                I agree to comply with{' '}
                <a
                  href="/seller/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline font-semibold"
                >
                  Kalinga Crafts Store Supplier Agreement
                </a>
              </span>
            </label>
            {errors.agreeToTerms && (
              <p className="text-red-500 text-sm mt-2">{errors.agreeToTerms}</p>
            )}
          </div>

          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600 text-sm">{errors.submit}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={onPrev}
              className="flex-1 py-2 px-4 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition"
            >
              Back
            </button>
            <button
              type="submit"
              disabled={loading || !formData.agreeToTerms}
              className={`
                flex-1 py-2 px-4 rounded-lg font-semibold text-white
                transition duration-200
                ${
                  loading || !formData.agreeToTerms
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }
              `}
            >
              {loading ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>

      <div className="mt-8">
        <FAQSection faqs={supplierFAQs} />
      </div>
    </div>
  );
};

export default SupplierDetailsForm;
